import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { Checkbox } from '@components/checkbox';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAttribute } from '@components/icons/attribute';
import { IconDocument } from '@components/icons/document';
import { IconDocumentCreate } from '@components/icons/document-create';
import { IconForm } from '@components/icons/form';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconReplace } from '@components/icons/replace';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconSettings } from '@components/icons/settings';
import { Menu } from '@components/menu';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { NoResultCard } from '@components/no-result-card';
import { SelectOption } from '@components/select';
import { forceHideSidebar, InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';
import { fileNameFromPath } from '@utils/strings';

import { useDocumentTemplates } from 'api/documents/hooks';
import {
  createJourneyActionDocumentPath,
  DocumentTemplate,
  DocumentTemplateField,
} from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed, JourneyActionValuesTypes } from 'api/types';
import { TemplateCardList } from 'components/template-card';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  createActionForm,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { TEMPLATE_PREVIEW_BUTTON_CLASSNAME, TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.create_document;

export const SidebarContent = (props: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { journeyActionId } = props;
  const { workspaceId } = useCurrentWorkspace();
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const queryString = useMemo(() => {
    return toQueryString({
      workspace: workspaceId,
    });
  }, [workspaceId]);
  const values = data?.values;
  const templatesQuery = useDocumentTemplates(queryString);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [idToReplace, setIdToReplace] = useState<string | null>(null);
  const templates = (templatesQuery.data ?? []).filter(
    (template) => template.shortId === selectedTemplateId || template.status === 'active'
  );
  const selectedTemplate = templates.find((template) => template.shortId === selectedTemplateId);
  const isReplace = !Boolean(selectedTemplate) && Boolean(idToReplace);

  const title = Boolean(selectedTemplate)
    ? t('createDocument')
    : isReplace
    ? t('replaceDocument')
    : t('selectDocument');
  const icon = Boolean(selectedTemplate)
    ? IconDocumentCreate
    : isReplace
    ? IconReplace
    : IconSelectSingle;

  useEffect(() => {
    if (values?.template?.value) {
      setSelectedTemplateId(values.template.value);
      setIdToReplace(values.template.value);
    }
  }, [values?.template?.value]);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} title={title} icon={icon} {...props}>
        {isReplace ? (
          <Button
            variant="ghost"
            text={t('core.cancel')}
            onClick={() => setSelectedTemplateId(idToReplace)}
          />
        ) : (
          <InfoButton>
            <Info />
          </InfoButton>
        )}
      </ActionHeader>
      <Sidebar.Content>
        {selectedTemplate ? (
          <Document
            journeyActionId={journeyActionId}
            action={data}
            selectedTemplate={selectedTemplate}
            switchTemplate={() => {
              setSelectedTemplateId(null);
            }}
          />
        ) : (
          <SelectDocument
            isReplace={isReplace}
            templates={templates}
            onSelect={(id: string) => {
              setSelectedTemplateId(id);
              setIdToReplace(id);
            }}
            idToReplace={idToReplace}
          />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

export const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutCreate')} />
      <Text text={t('allowsToCreate')} />
      <Text text={t('needsEmptyFields')} />
      <Text text={t('mustHaveBracekts')} />
      <Stack space={1}>
        <Text font="bold14" text={t('example')} />
        <Text text="{{full_name}}" />
        <Text text="{{email}}" />
      </Stack>
    </Stack>
  );
};

export const SelectDocument = ({
  templates,
  onSelect,
  idToReplace,
  isReplace,
}: {
  templates: DocumentTemplate[];
  onSelect: (templateId: string) => void;
  idToReplace: string | null;
  isReplace?: boolean;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { workspaceId } = useCurrentWorkspace();

  return (
    <TemplateCardList
      selectedId={idToReplace ?? ''}
      onSelect={onSelect}
      templates={templates}
      noResults={(search) => (
        <NoResultCard icon={IconForm} text={!search ? t('noDocuments') : t('noDocumentsSearch')} />
      )}
      actionButton={
        <Button
          variant="secondary"
          iconLeft={isReplace ? <IconDocument /> : <IconOpenNewTab />}
          text={isReplace ? t('createDocument') : undefined}
          href={generatePath(routePaths.workspaceDocuments, {
            workspaceId,
          })}
          target="_blank"
        />
      }
    />
  );
};

export const TemplateCard = ({
  selectedTemplate,
  switchTemplate,
}: {
  selectedTemplate: DocumentTemplate;
  switchTemplate?: () => void;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { workspaceId } = useCurrentWorkspace();
  const showFilePreviewModal = usePreviewFileModal();
  const previewUrl = selectedTemplate.previewUrl;
  const configureUrl = generatePath(routePaths.worksspaceDocumentDetail, {
    workspaceId,
    documentId: selectedTemplate.shortId,
    '*': '',
  });
  return (
    <Card spaceX={4} spaceY={2}>
      <Stack orientation="horizontal" justify="space-between">
        <Stack align="center" space={2} orientation="horizontal">
          <IconForm />
          <div>
            <Text font="semibold14" text={selectedTemplate.name} />
            <Text font="regular14" text={selectedTemplate.description ?? ''} />
          </div>
        </Stack>
        <Stack orientation="horizontal" justify="flex-end">
          {previewUrl && (
            <IconButton
              className={TEMPLATE_PREVIEW_BUTTON_CLASSNAME}
              variant="ghost-purple"
              onClick={() => {
                showFilePreviewModal({
                  formFile: {
                    name: fileNameFromPath(previewUrl),
                    src: previewUrl,
                  },
                  filesList: [{ name: fileNameFromPath(previewUrl), src: previewUrl }],
                });
              }}
              icon={<IconOpenPreview />}
            />
          )}
          <Menu
            dataTestid="template-card-menu"
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
            }}
          >
            {switchTemplate && (
              <Menu.Item
                label={t('replace')}
                onSelect={() => switchTemplate()}
                icon={<IconReplace />}
              />
            )}
            <Menu.Item
              label={t('configure')}
              onSelect={() => window.open(configureUrl, '_blank')}
              icon={<IconSettings />}
            />
          </Menu>
        </Stack>
      </Stack>
    </Card>
  );
};

type CreateDocumentData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  template: string;
  selectedTemplateShortId: string;
} & Record<string, string> &
  TimeSavedData;

const Form = createActionForm<CreateDocumentData>();

const Document = ({
  journeyActionId,
  action,
  selectedTemplate,
  switchTemplate,
}: {
  journeyActionId: string;
  action: JourneyActionDetailed<typeof ACTION_TYPE>;
  selectedTemplate: DocumentTemplate;
  switchTemplate: () => void;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const fields = selectedTemplate.fieldConfig;
  const values = action.values as JourneyActionValuesTypes['create_document'];
  const [rename, setRename] = useState(Boolean(values?.name));
  const formStore = useCreateActionFormStore();
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty || rename !== Boolean(values?.name);
  const editMutation = useUpdateJourneyAction(journeyActionId, { onSuccess: () => false });
  const fieldMappingId = getFieldMappingId(action.journey);
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['phone'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['date'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['select'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['file'],
  });
  const numberVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['number'],
  });
  const decimalVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['number', 'decimal'],
  });

  useEffect(() => {
    // Set the currently selected template from the other page into form state
    // This will make it dirty if the template selected isn't the same as previously
    formStore.actions.setValue('selectedTemplateShortId', selectedTemplate.shortId);
  }, [formStore.actions, selectedTemplate.shortId]);

  const getVariableGroups = (fieldType: string) => {
    switch (fieldType) {
      case 'email':
        return emailVariableGroups;
      case 'phone':
        return phoneVariableGroups;
      case 'date':
        return dateVariableGroups;
      case 'select':
        return optionsVariableGroups;
      case 'file':
        return fileVariableGroups;
      case 'number':
        return numberVariableGroups;
      case 'decimal':
        return decimalVariableGroups;
      default:
        return variableGroups;
    }
  };

  const extractTemplateVariables = (data: CreateDocumentData) => {
    const prefix = 'templateVariables_';
    const templateVariables: { [key: string]: { value: string; variables: object } } = {};

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.substring(prefix.length);
        const value = data[key];
        if (!value) return;
        else if (typeof value !== 'object') {
          templateVariables[newKey] = { value, variables: {} };
        } else {
          templateVariables[newKey] = value;
        }
      }
    });

    return templateVariables;
  };

  const handleSave = (data: CreateDocumentData, shouldSkipClosing?: boolean) => {
    editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: ACTION_TYPE,
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        template: { value: data.selectedTemplateShortId },
        templateVariables: extractTemplateVariables(data),
        name: rename ? data.name : undefined,
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });
    !shouldSkipClosing && forceHideSidebar();
  };

  // Hack start - Even worse hacky method to save the document if it hasn't been saved before
  useEffect(() => {
    if (!values) {
      const formData = formStore.getState().values as CreateDocumentData;
      handleSave(formData, true);
    }
    // eslint-disable-next-line
  }, [values]);
  // Hack end

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <VisuallyHidden>
        <Form.TextField
          id="selectedTemplateShortId"
          label="Selected template"
          defaultValue={action.values?.template?.value ?? ''}
        />
      </VisuallyHidden>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            <DescriptionFormField
              defaultValue={
                action.description ?? selectedTemplate.description ?? selectedTemplate.name
              }
            />
            <TemplateCard selectedTemplate={selectedTemplate} switchTemplate={switchTemplate} />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('documentFields')}>
          {fields.length === 0 && (
            <NoResultCard icon={IconAttribute} text={t('noSupportedFields')} />
          )}
          <Stack space={4}>
            {fields.map((field) => (
              <DocumentField
                key={`${field.id}-${field.type}`}
                field={field}
                defaultValue={
                  action.values?.templateVariables?.[field.id] ?? {
                    value: selectedTemplate.defaultData?.[field.id] ?? '',
                  }
                }
                variableGroups={getVariableGroups(field.type)}
                fieldMappingId={fieldMappingId}
                journeyActionId={journeyActionId}
                journeyId={action.journey}
              />
            ))}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('advanced')} defaultClosed={!rename}>
          <Stack space={4}>
            <Checkbox label={t('renameOutput')} onChange={setRename} defaultValue={rename} />
            {rename && (
              <Form.FieldMappingTextEditor
                id="name"
                plaintext
                singleLine
                label={t('fileOutputName')}
                defaultValue={values?.name}
                variableGroups={variableGroups}
                validators={['required']}
                fieldMappingId={fieldMappingId}
              />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...action.timeSaved} />
      </Stack>
      <ActionFooter action={action} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

type DocumentFieldProps = {
  field: DocumentTemplateField;
  defaultValue?: VariableValue;
  variableGroups: FieldMappingGroup[];
  fieldMappingId: string;
  journeyActionId: string;
  journeyId: string;
};

export const DocumentField = ({
  field,
  defaultValue,
  variableGroups,
  fieldMappingId,
  journeyActionId,
  journeyId,
}: DocumentFieldProps) => {
  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(journeyId, journeyActionId),
    true
  );
  switch (field.type) {
    case 'number':
    case 'decimal':
      return (
        <Form.FieldMappingNumberField
          id={`templateVariables_${field.id}`}
          label={field.label}
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
          isDecimal={field.type === 'decimal'}
        />
      );
    case 'select':
      return (
        <Form.FieldMappingSelectField
          id={`templateVariables_${field.id}`}
          label={field.label}
          options={field.options ?? []}
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
    case 'date':
      return (
        <Form.FieldMappingDatePicker
          id={`templateVariables_${field.id}`}
          label={field.label}
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
    case 'email':
      return (
        <Form.EmailField
          id={`templateVariables_${field.id}`}
          label={field.label}
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
    case 'phone':
      return (
        <Form.FieldMappingPhoneField
          id={`templateVariables_${field.id}`}
          label={field.label}
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
    case 'file':
      return (
        <Form.FieldMappingFileField
          id={`templateVariables_${field.id}`}
          label={field.label}
          defaultValue={defaultValue?.value === '' ? undefined : defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
          onSelect={uploadMutation.mutateAsync}
        />
      );
    case 'paragraph':
      return (
        <Form.FieldMappingTextEditor
          id={`templateVariables_${field.id}`}
          plaintext
          label={field.label}
          fieldType="paragraph"
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
    case 'text':
      return (
        <Form.FieldMappingTextEditor
          id={`templateVariables_${field.id}`}
          plaintext
          label={field.label}
          singleLine
          defaultValue={defaultValue}
          variableGroups={variableGroups}
          fieldMappingId={fieldMappingId}
        />
      );
  }
};

const sidebarLocale = {
  en: {
    selectDocument: 'Select document',
    createDocument: 'Create document',
    aboutCreate: 'About creating documents',
    allowsToCreate: 'Allows you to create documents from a template',
    needsEmptyFields:
      'The document needs to have empty fields that can receive values from the journey, these values can then be mapped to values from the journey, for example from a form that is filled out.',
    mustHaveBracekts:
      'Fields that are mappable must be declared with two brackets before and after the field',
    example: 'Example',
    noDocuments: 'You do not have any documents to create from',
    noDocumentsSearch: 'You do not have any documents that match your current search',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    documentFields: 'Document fields',
    noSupportedFields: 'Selected document does not have any\nsupported fields',
    advanced: 'Advanced',
    renameOutput: 'Rename document output',
    fileOutputName: 'File output name',
    replace: 'Replace',
    replaceDocument: 'Replace document',
    configure: 'Configure',
  },
};
